import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { boolean, object, string } from "yup";
import { ExpenseFormValues } from "src/models/Expense.model";
import ExpenseForm from "../Layout/ExpenseForm";
import {
  useViewExpenseByIdQuery,
  useEditExpenseMutation,
} from "src/services/ExpensesServices";
import { showToast } from "src/utils/showToaster";
import { CircularProgress } from "@mui/material";
import moment from "moment";

type Props = {
  onClose: () => void;
  expenseId: string;
};

const EditExpenseFormWrapper = ({ onClose, expenseId }: Props) => {
  const [expenseData, setExpenseData] = useState<any>();
  const [editExpense] = useEditExpenseMutation();
  const { data, isLoading, isFetching } = useViewExpenseByIdQuery(expenseId);
  useEffect(() => {
    if (!isLoading && !isFetching) {
      setExpenseData(data?.data);
    }
  }, [isFetching, isLoading, data]);
  // Form Initial Values
  const initialValues: ExpenseFormValues = {
    amount: expenseData?.amount,
    expense_on_date: expenseData?.expense_on_date,
    categoryId: {
      value: expenseData?.categoryId,
      label: expenseData?.expenceCategoryLabel,
    },
    accountId: {
      value: expenseData?.accountId,
      label: expenseData?.accountLabel,
    },
    branchId: {
      value: expenseData?.branchId,
      label: expenseData?.branchName,
    },
    remark: expenseData?.remark,
    will_return: expenseData?.will_return,
    return_date: expenseData?.return_date,
    return_remark: expenseData?.return_remark,
    return_amount: expenseData?.willReturnAmount,
  };

  // Validation Schema
  const validationSchema = object().shape({
    amount: string().required("Please enter amount"),
    categoryId: object().required("Please select category"),
    accountId: object().required("Please select account"),
    branchId: object().required("Please select branch"),
    remark: string().required("Please enter remark"),
    will_return: boolean(),
    expense_on_date: string().required("Please select date"),
  });

  // Handle Submit
  const handleSubmit = (
    values: ExpenseFormValues,
    { setSubmitting, resetForm }: FormikHelpers<ExpenseFormValues>
  ) => {
    const formattedValues = {
      categoryId: values?.categoryId?.value,
      accountId: values?.accountId?.value,
      expense_on_date: values?.expense_on_date
        ? moment(values?.expense_on_date).format("YYYY-MM-DD")
        : "",
      branchId: values?.branchId?.value,
      amount: parseInt(values.amount),
      remark: values.remark,
      will_return: values?.will_return,
      return_date: values?.return_date === null ? "" : values?.return_date,
      return_remark: values?.return_remark,
      willReturnAmount:
        values?.return_amount === "" ? 0 : values?.return_amount,
    };
    editExpense({
      body: formattedValues,
      id: expenseId,
    }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          onClose();
          resetForm();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            {(isLoading || isFetching) && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}
            <ExpenseForm
              formType="Edit"
              formikProps={formikProps}
              onClose={onClose}
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditExpenseFormWrapper;
